import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { CaretLeft, CaretRight, Printer } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/date";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const mondayOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const iso = (d) => d.toISOString().slice(0, 10);
const rateUnit = (t) => /hour|hr/i.test(t || "") ? "hr" : /week/i.test(t || "") ? "wk" : "day";

export default function WeeklySummaryView({ projectId }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/weekly-summary`, { params: { week_start: iso(weekStart) } });
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [projectId, weekStart]);
  useEffect(() => { load(); }, [load]);

  const shift = (n) => setWeekStart((w) => { const x = new Date(w); x.setDate(x.getDate() + n * 7); return x; });
  const thisWeek = () => setWeekStart(mondayOf(new Date()));

  if (!projectId) return <div className="p-8 text-center text-ink-tertiary text-[13px]">Select a project to generate its weekly hire summary.</div>;

  const p = data?.project || {};
  const meta = [
    ["Project", p.name], ["Site", p.site_address || p.site || "—"], ["Client", p.client || "—"],
    ["PO", p.customer_po || "—"], ["Project #", p.project_no || "—"],
    ["Week", data ? `${fmtDate(data.week_start)} – ${fmtDate(data.week_end)}` : "—"],
  ];
  const num = "px-3 py-2.5 text-right tabular-nums whitespace-nowrap";
  const numHead = "px-3 py-2.5 text-right font-medium text-ink-muted uppercase tracking-[0.08em] text-[10.5px] whitespace-nowrap";

  return (
    <div className="space-y-4 dashboard-print" data-testid="weekly-summary-view">
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="weekly-prev" className="h-8 w-8 border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px] flex items-center justify-center"><CaretLeft size={15} weight="bold" /></button>
          <span className="text-[13px] font-mono font-semibold text-ink-primary min-w-[220px] text-center" data-testid="weekly-range">
            {data ? `${fmtDate(data.week_start)} – ${fmtDate(data.week_end)}` : "…"}
          </span>
          <button onClick={() => shift(1)} data-testid="weekly-next" className="h-8 w-8 border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px] flex items-center justify-center"><CaretRight size={15} weight="bold" /></button>
          <button onClick={thisWeek} data-testid="weekly-this-week" className="h-8 px-3 border border-border-control text-[11px] font-bold uppercase tracking-[0.04em] text-ink-secondary hover:bg-surface-panel rounded-[7px]">This Week</button>
        </div>
        <button onClick={() => window.print()} data-testid="weekly-print"
          className="h-9 px-4 bg-equipr-yellow text-black font-bold text-[12px] uppercase tracking-[0.05em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark inline-flex items-center gap-1.5">
          <Printer size={16} weight="bold" /> Print / Save PDF
        </button>
      </div>

      {/* Invoice card */}
      <div className="bg-surface-card border border-border rounded-[14px] shadow-card p-8" data-testid="weekly-invoice">
        <div className="flex items-start justify-between gap-6 mb-7">
          <div>
            <div className="text-[28px] font-extrabold tracking-tight text-ink-primary leading-none">EQUIP<span className="text-accent-ink">R</span></div>
            <h2 className="text-[24px] font-bold text-ink-primary mt-3">Weekly hire summary</h2>
          </div>
          <table className="text-[13px]">
            <tbody>
              {meta.map(([k, v]) => (
                <tr key={k}>
                  <td className="text-right pr-4 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted align-middle">{k}</td>
                  <td className="py-0.5 font-semibold text-ink-primary text-right">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading ? <div className="py-16 text-center text-ink-tertiary">Loading…</div> :
          !data || data.rows.length === 0 ? <div className="py-16 text-center text-ink-tertiary" data-testid="weekly-empty">No hire activity for this project in the selected week.</div> : (
          <>
            <table className="w-full text-[13px] border-t-2 border-ink-primary">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2.5 text-left font-medium text-ink-muted uppercase tracking-[0.08em] text-[10.5px]">Resource</th>
                  <th className="px-3 py-2.5 text-left font-medium text-ink-muted uppercase tracking-[0.08em] text-[10.5px]">Type</th>
                  <th className="px-3 py-2.5 text-left font-medium text-ink-muted uppercase tracking-[0.08em] text-[10.5px]">Supplier</th>
                  {["Days", "Appr. hrs", "Prov. hrs", "Downtime", "S/Down", "Rate", "Line Total"].map((h) => <th key={h} className={numHead}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-divider-row" data-testid={`weekly-row-${i}`}>
                    <td className="px-3 py-2.5 font-mono font-bold text-ink-primary whitespace-nowrap">{r.label}</td>
                    <td className="px-3 py-2.5 text-ink-secondary">{r.item}{r.hire_type ? ` · ${r.hire_type}` : ""}</td>
                    <td className="px-3 py-2.5 text-ink-secondary">{r.supplier || "—"}</td>
                    <td className={num + " text-ink-secondary"}>{r.days_on_hire || "—"}</td>
                    <td className={num + " font-semibold text-ink-primary"}>{r.approved_hours || "—"}</td>
                    <td className={num + " text-[#8A5B00]"}>{r.provisional_hours || "—"}</td>
                    <td className={num + " text-ink-tertiary"}>{r.downtime_hours || "—"}</td>
                    <td className={num}>{r.standdown_days ? <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-[6px] bg-[#FBE0E0] text-[#B42318] text-[11px] font-bold">{r.standdown_days}</span> : <span className="text-ink-tertiary">—</span>}</td>
                    <td className={num + " text-ink-secondary"}>{r.rate ? `${fmt(r.rate)} / ${rateUnit(r.rate_type)}` : "—"}</td>
                    <td className={num + " font-bold text-ink-primary"}>{fmt(r.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mt-6">
              <div className="w-full max-w-[360px] space-y-2">
                <div className="flex items-center justify-between px-3 text-[14px]"><span className="text-ink-secondary">Subtotal</span><span className="font-semibold text-ink-primary tabular-nums" data-testid="weekly-subtotal">{fmt(data.subtotal)}</span></div>
                <div className="flex items-center justify-between px-3 text-[14px]"><span className="text-ink-secondary">GST {data.gst_percent ?? 10}%</span><span className="font-semibold text-ink-primary tabular-nums" data-testid="weekly-gst">{fmt(data.gst)}</span></div>
                <div className="flex items-center justify-between px-3 py-3 rounded-[10px] bg-[#FBF2CC] border border-[#EAD79A]">
                  <span className="font-bold text-ink-primary uppercase tracking-[0.04em] text-[13px]">Total inc GST</span>
                  <span className="font-extrabold text-ink-primary text-[20px] tabular-nums" data-testid="weekly-total">{fmt(data.total)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 pt-4 text-[11.5px] text-ink-tertiary">
              <span>Generated {fmtDateTime(data.generated_at)} · EQUIPR Civil</span>
              <span>Provisional hours are unapproved dockets and may change.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
